import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, Pause, Play } from "lucide-react";
import { Stat, Section, Loading, Empty, Pill, useFetch } from "./_shared";

const STATUS_TONE = { active: "good", paused: "warn", completed: "default" };
const PROTECTED = ["founder_50", "founder_500"];

function CreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", program_prop: "", description: "", price_type: "free", price_amount: "", place_limit: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (f.name.trim().length < 2 || f.description.trim().length < 10 || f.program_prop.trim().length < 2) {
      toast.error("Name, Prop (≥2) und Beschreibung (≥10 Zeichen) sind Pflicht.");
      return;
    }
    setBusy(true);
    try {
      const config = {
        price_type: f.price_type,
        price_amount: f.price_type === "paid" ? Number(f.price_amount || 0) : 0,
        price_currency: "EUR",
        place_limit: f.place_limit ? Number(f.place_limit) : null,
      };
      await api.post("/admin/programs", { name: f.name.trim(), program_prop: f.program_prop.trim(), description: f.description.trim(), config });
      toast.success("Programm angelegt.");
      setOpen(false);
      setF({ name: "", program_prop: "", description: "", price_type: "free", price_amount: "", place_limit: "" });
      onCreated();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5 rounded-full" data-testid="admin-prog-new"><Plus className="h-4 w-4" /> Neues Programm</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Neues Programm</DialogTitle><DialogDescription>Lege ein neues Founder-/VIP-Programm mit Preis und Platzlimit an.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name (z. B. VIP Circle)" value={f.name} onChange={set("name")} data-testid="admin-prog-name" />
          <Input placeholder="program_prop (z. B. vipcircle)" value={f.program_prop} onChange={set("program_prop")} data-testid="admin-prog-prop" />
          <Textarea placeholder="Beschreibung (min. 10 Zeichen)" value={f.description} onChange={set("description")} className="min-h-[90px]" data-testid="admin-prog-desc" />
          <div className="flex gap-2">
            <select value={f.price_type} onChange={set("price_type")} className="h-10 flex-1 rounded-lg border border-border bg-background px-2 text-sm text-foreground" data-testid="admin-prog-price-type">
              <option value="free">Kostenlos</option><option value="paid">Kostenpflichtig</option>
            </select>
            {f.price_type === "paid" && <Input type="number" placeholder="€ Betrag" value={f.price_amount} onChange={set("price_amount")} className="w-28" data-testid="admin-prog-price-amount" />}
          </div>
          <Input type="number" placeholder="Platzlimit (leer = ∞)" value={f.place_limit} onChange={set("place_limit")} data-testid="admin-prog-limit" />
        </div>
        <DialogFooter>
          <Button className="rounded-full" disabled={busy} onClick={submit} data-testid="admin-prog-create">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Programs() {
  const { data, loading, reload } = useFetch("/admin/programs");
  const [busy, setBusy] = useState(null);
  if (loading) return <Loading />;
  const programs = data?.programs || [];
  const totApps = programs.reduce((a, p) => a + (p.stats?.applications || 0), 0);
  const totApproved = programs.reduce((a, p) => a + (p.stats?.approved || 0), 0);

  const setStatus = async (id, status) => {
    setBusy(id);
    try { await api.post(`/admin/programs/${id}/status`, { status }); toast.success("Status aktualisiert."); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  const seedDefaults = async () => {
    setBusy("seed");
    try { const { data: r } = await api.post("/admin/programs/seed-defaults"); toast.success(`${r.seeded} Standard-Programm(e) angelegt.`); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  const del = async (id) => {
    if (!window.confirm("Programm wirklich löschen?")) return;
    setBusy(id);
    try { await api.delete(`/admin/programs/${id}`); toast.success("Gelöscht."); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Programme" subtitle="Founder-/VIP-Programme, Bewerbungen & Affiliate."
        action={<div className="flex gap-2"><Button size="sm" variant="outline" className="rounded-full" disabled={busy === "seed"} onClick={seedDefaults} data-testid="admin-prog-seed">Standard anlegen</Button><CreateDialog onCreated={reload} /></div>} />

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Programme" value={programs.length} testId="admin-prog-count" />
        <Stat label="Bewerbungen" value={totApps} />
        <Stat label="Angenommen" value={totApproved} tone="good" />
      </div>

      <Section title={`Alle Programme (${programs.length})`} testId="admin-prog-list">
        {programs.length === 0 ? <Empty text="Keine Programme. Lege Standard-Programme an." /> : (
          <div className="space-y-2">
            {programs.map((p) => {
              const s = p.stats || {};
              return (
                <div key={p.id} data-testid={`admin-prog-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                  <Link to={`/app/admin/programs/${p.id}`} className="min-w-0 flex-1 group" data-testid={`admin-prog-open-${p.id}`}>
                    <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground group-hover:underline">{p.name} <Pill tone={STATUS_TONE[p.status] || "default"}>{p.status}</Pill> <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /></p>
                    <p className="truncate text-xs text-muted-foreground">{p.program_prop} · {s.applications || 0} Bewerb. · {s.pending || 0} offen · {s.approved || 0} angenommen · {s.registered_users || 0} Nutzer</p>
                  </Link>
                  <div className="flex items-center gap-1.5">
                    {p.status === "active"
                      ? <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === p.id} onClick={() => setStatus(p.id, "paused")} data-testid={`admin-prog-pause-${p.id}`}><Pause className="h-4 w-4" /></Button>
                      : <Button size="sm" variant="outline" className="gap-1.5 rounded-full text-emerald-600" disabled={busy === p.id} onClick={() => setStatus(p.id, "active")} data-testid={`admin-prog-activate-${p.id}`}><Play className="h-4 w-4" /></Button>}
                    {!PROTECTED.includes(p.id) && (
                      <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === p.id} onClick={() => del(p.id)} data-testid={`admin-prog-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
